<template>
  <div class="deal-name-cell flex items-center space-x-2">
    <div
      class="max-w-120 overflow-hidden font-medium text-ellipsis whitespace-nowrap"
      data-cy="deal-name"
    >
      <router-link :to="dealRoute">
        {{ value || "---" }}
      </router-link>
    </div>
    <copy-to-clipboard-button
      v-if="showCopyButtons && value"
      class="copy-button"
      :tooltip="nameTooltip"
      :value="value"
      size="sm"
    />
    <copy-to-clipboard-button
      v-if="showCopyButtons && fullDealLink"
      class="copy-button"
      icon="link"
      :tooltip="dealLinkTooltip"
      :value="fullDealLink"
      size="sm"
    />
  </div>
</template>

<script lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";

import { ROUTE_DEAL_PROGRESS } from "@/router/routes";

import type { IApplication } from "@/models/applications";
import type { PropType } from "vue";
</script>

<script setup lang="ts">
import CopyToClipboardButton from "@/components/CopyToClipboardButton.vue";

const props = defineProps({
  value: {
    type: String,
    required: true,
  },
  model: {
    type: Object as PropType<IApplication>,
    required: true,
  },
  includeBackTo: {
    type: Boolean,
    default: false,
  },
  showCopyButtons: {
    type: Boolean,
    default: true,
  },
});

const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const dealRoute = computed<
  string | { path: string; query: Record<string, string> }
>(() => {
  const id = props.model.id;

  const path = `/deals/${id}/progress`;

  return props.includeBackTo
    ? { path, query: { backTo: route.fullPath } }
    : path;
});

const fullDealLink = computed(() => {
  const id = props.model.id;
  const route = router.resolve({
    name: ROUTE_DEAL_PROGRESS,
    params: { id },
  });
  return new URL(route.href, window.location.origin).href;
});

const nameTooltip = computed(() => {
  return t("COMMON.COPY_TO_CLIPBOARD", {
    type: `${t("COMMON.DEAL")}  ${t("COMMON.NAME")}`,
  });
});

const dealLinkTooltip = computed(() => {
  return t("COMMON.COPY_TO_CLIPBOARD", {
    type: `${t("COMMON.DEAL")}  ${t("COMMON.LINK")}`,
  });
});
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.text-ellipsis {
  text-overflow: ellipsis;
}

.copy-button {
  @apply invisible p-1 opacity-0;
}

.deal-name-cell:hover .copy-button {
  @apply visible opacity-100;
}
</style>
